import httpClient from '../httpClient';
import { AxiosRequestConfig } from 'axios';
import { getFileParams, uploadFileToUrl } from 'uploadFileUtils';
import {
  CreateNativeDataRoomNdaBody,
  DataRoomNda,
  DataRoomNdaSigner,
  DataRoomNdaSigningStatus,
  UploadNdaFile,
  UploadNdaFileWithoutCreate,
} from '../../models/DataRoomNDA';
import { DropzoneFile } from 'components/Dropzone/utils';
import { PaginatedData, PaginationParams } from 'models/PaginatedData';
import { Params } from 'react-router-dom';

function getDataRoomNdas(dataRoomId: string, includeInactive?: boolean) {
  return httpClient.get<DataRoomNda[]>(
    `data-room-ndas/data-rooms/${dataRoomId}`,
    { params: { includeInactive } },
  );
}

async function uploadNda(
  dataRoomId: string,
  file: File,
  config?: AxiosRequestConfig,
): Promise<DataRoomNda | null> {
  let dataRoomNdaId;

  try {
    const fileParams = getFileParams('dataRoomNda', file);

    const { data } = await httpClient.post<UploadNdaFile>(
      `data-room-ndas/data-rooms/${dataRoomId}/upload-temporary-object`,
      fileParams,
    );

    const { presignedPost, dataRoomNda } = data;

    dataRoomNdaId = dataRoomNda?._id;

    await uploadFileToUrl(presignedPost, file, config);

    return dataRoomNda;
  } catch {
    if (dataRoomNdaId) deleteNda(dataRoomNdaId);

    return null;
  }
}

function createNativeNda(
  dataRoomId: string,
  { file, fields, name, uploadLocation }: CreateNativeDataRoomNdaBody,
) {
  const formData = new FormData();

  formData.append('file', file);
  formData.append('fields', JSON.stringify(fields));
  formData.append('uploadLocation', uploadLocation);

  if (name) formData.append('name', name);

  return httpClient.post<DataRoomNda>(
    `data-room-ndas/data-rooms/${dataRoomId}/native`,
    formData,
  );
}

function deleteNda(dataRoomNdaId: string) {
  return httpClient.delete(`data-room-ndas/${dataRoomNdaId}`);
}

function linkEnvelopeTemplate(
  dataRoomNdaId: string,
  envelopeTemplateId: string,
  envelopeTemplateName: string,
) {
  return httpClient.post(
    `data-room-ndas/${dataRoomNdaId}/link-envelope-template`,
    {
      envelopeTemplateId,
      envelopeTemplateName,
    },
  );
}

function getNdaSigners(dataRoomId: string, params: PaginationParams & Params) {
  return httpClient.get<PaginatedData<DataRoomNdaSigner>>(
    `data-room-nda-signers/data-rooms/${dataRoomId}`,
    { params },
  );
}

function sendNdaReminder(signingId: string) {
  return httpClient.post(`data-room-nda-signings/${signingId}/send-reminder`);
}

function sendReminders(dataRoomId: string, signerIds: string[]) {
  return httpClient.post(
    `data-room-ndas/data-rooms/${dataRoomId}/send-reminders`,
    { signerIds },
  );
}

function overrideNdaStatus(
  dataRoomId: string,
  signerIds: string[],
  dataRoomNdaIds: string[],
  status: DataRoomNdaSigningStatus,
) {
  return httpClient.post(
    `data-room-ndas/data-rooms/${dataRoomId}/override-status`,
    { signerIds, dataRoomNdaIds, status },
  );
}

async function uploadNdaWithoutCreate(
  dataRoomId: string,
  file: File,
  config?: AxiosRequestConfig,
): Promise<{ uploadedFile: DropzoneFile; temporaryFileId: string } | null> {
  try {
    const fileParams = getFileParams('dataRoomNda', file);

    const { data } = await httpClient.post<UploadNdaFileWithoutCreate>(
      `data-room-ndas/data-rooms/${dataRoomId}/upload-temporary-object-without-create`,
      fileParams,
    );

    const { presignedPost, uploadedFile, temporaryFileId } = data;

    await uploadFileToUrl(presignedPost, file, config);

    return { uploadedFile, temporaryFileId };
  } catch {
    return { uploadedFile: null, temporaryFileId: null };
  }
}

function submitNdaUpload(dataRoomId: string, temporaryFileId: string) {
  return httpClient.post(
    `data-room-ndas/data-rooms/${dataRoomId}/submit-upload`,
    { temporaryFileId },
  );
}

function linkNdaWithDataRoom(
  dataRoomId: string,
  envelopeTemplateId: string,
  envelopeTemplateName: string,
) {
  return httpClient.post(
    `data-room-ndas/data-rooms/${dataRoomId}/link-envelope-template`,
    { envelopeTemplateId, envelopeTemplateName },
  );
}

function getNdaSignerNameOptions(dataRoomId: string, searchString: string) {
  return httpClient.get<PaginatedData<string>>(
    `data-room-nda-signers/data-rooms/${dataRoomId}/nda-signers-names`,
    { params: { $searchString: searchString } },
  );
}

function getNdaSignerEmailOptions(dataRoomId: string, searchString: string) {
  return httpClient.get<PaginatedData<string>>(
    `data-room-nda-signers/data-rooms/${dataRoomId}/nda-signers-emails`,
    { params: { $searchString: searchString } },
  );
}

export default {
  getDataRoomNdas,
  uploadNda,
  createNativeNda,
  deleteNda,
  linkEnvelopeTemplate,
  getNdaSigners,
  sendNdaReminder,
  sendReminders,
  overrideNdaStatus,
  uploadNdaWithoutCreate,
  submitNdaUpload,
  linkNdaWithDataRoom,
  getNdaSignerNameOptions,
  getNdaSignerEmailOptions,
};
